import { useState, useMemo } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useJobs } from '@/hooks/useJobs';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface MatchJobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  calendarId: string;
  eventName: string;
  onMatchSuccess: () => void;
}

export function MatchJobModal({
  open,
  onOpenChange,
  eventId,
  calendarId,
  eventName,
  onMatchSuccess,
}: MatchJobModalProps) {
  const { data: jobs, isLoading } = useJobs();
  const { toast } = useToast();
  const [searchText, setSearchText] = useState('');
  const [selectedJobCode, setSelectedJobCode] = useState<string | null>(null);
  const [isMatching, setIsMatching] = useState(false);

  // Filter jobs based on search text (job code, address, and description)
  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    if (!searchText) return jobs;

    const lowerSearch = searchText.toLowerCase();
    return jobs.filter((job) => {
      const jobCode = job.jobCode?.toLowerCase() || '';
      const address = job.location.addressLine1?.toLowerCase() || '';
      const description = job.description?.toLowerCase() || '';
      return jobCode.includes(lowerSearch) || address.includes(lowerSearch) || description.includes(lowerSearch);
    });
  }, [jobs, searchText]);

  const handleMatch = async () => {
    if (!selectedJobCode) {
      toast({
        title: 'No job selected',
        description: 'Please select a job to match',
        variant: 'destructive',
      });
      return;
    }

    setIsMatching(true);
    try {
      // Remove any existing job code from the event name (from beginning or end)
      let cleanedEventName = eventName
        .replace(/^\[([A-Z]+-\d{2}-\d{3})\]\s*/, '') // Remove from beginning
        .replace(/\s*\[([A-Z]+-\d{2}-\d{3})\]$/, ''); // Remove from end

      // Update the event name to include the job code at the end
      const newSummary = `${cleanedEventName} [${selectedJobCode}]`;

      await apiClient.patch(`/calendar/events/${eventId}`, {
        calendarId,
        newSummary,
      });

      toast({
        title: 'Event matched successfully',
        description: `Event has been matched to job ${selectedJobCode}`,
      });

      onMatchSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error matching event:', error);
      toast({
        title: 'Match failed',
        description: error instanceof Error ? error.message : 'Failed to match event to job',
        variant: 'destructive',
      });
    } finally {
      setIsMatching(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isMatching) {
      setSelectedJobCode(null);
      setSearchText('');
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Match Event to Job</DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Event: <span className="font-medium">{eventName}</span>
          </p>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Filter by job code, address, or description..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Jobs Table */}
        <div className="flex-1 overflow-y-auto border rounded-md">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              {searchText ? 'No jobs found matching your search' : 'No jobs available'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Job Code</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedJobCode(job.jobCode)}
                  >
                    <TableCell>
                      <input
                        type="radio"
                        name="jobSelection"
                        checked={selectedJobCode === job.jobCode}
                        onChange={() => setSelectedJobCode(job.jobCode)}
                        className="h-4 w-4 cursor-pointer"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{job.jobCode}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>
                          {job.location.addressLine1}
                          {job.location.addressLine2 && ` ${job.location.addressLine2}`}
                        </span>
                        {job.location.city && (
                          <span className="text-sm text-gray-600">{job.location.city}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {job.description || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isMatching}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMatch}
            disabled={!selectedJobCode || isMatching}
          >
            {isMatching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Match
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
